// src/store/useUserStore.ts
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import api from "../api/api";
import { toast } from "react-toastify";
import { AxiosError } from "axios";

type Profile = {
  name: string;
  email: string;
  ic_number: string;
  birthday: string;
  profile_pic: string | null;
  phone: string;
};

type Student = {
  birthday?: string | null;
  email?: string | null;
  id: number;
  is_active?: boolean | null;
  name: string | null;
  payment_status?: string | null;
  profile_pic?: string | null;
  student_standard?: string | null;
  // optional field used in logic
  school_id?: string | number;
};

interface Voucher {
  id: string;
  type: string;
  amount: string;
  terms: string;
  academy: string;
  description: string;
  recommended?: boolean;
}

interface ProfileStore {
  // util
  loading: boolean;
  setLoading: (loading: boolean) => void;
  logout: () => void;

  // course related
  courses: [];
  setCourses: (data: []) => void;
  selectedStudent: null | Student;
  setSelectedStudent: (data: Student | null) => void;
  lastVisitedCourseId: string | null;
  setLastVisitedCourseId: (path: string | null) => void;
  clearLastVisitedCourse: () => void;


  // save meal and transport id
  mealId: string | null;
  saveMealId: (data: string) => void;
  transportId: string | null;
  saveTransportId: (data: string) => void;

  // Academy's Branch Course usage
  selectedBranchName: null | string;
  setSelectedBranchName: (data: string | null) => void;

  // user profile related
  profile: Profile | null;
  fetchProfile: () => Promise<void>;
  students: [] | Student[];
  setStudents: (data: Student[]) => void;
  fetchStudents: () => Promise<Student[]>;
  fetchStudentsRefresh: () => Promise<Student[]>;
  homePageShow: string;
  hasStudentWithSchoolId: boolean;
  setHomePageShow: (data: "school" | "academy" | "explore" | "") => void;
  setProfile: (partial: Partial<Profile>) => void;

  // cart edit
  cartEdit: any;
  setCartEdit: (data: any) => void;

  // attendance
  attendanceData: {
    studentName: string;
    studentId: number;
    eventId: number;
  } | null;
  setAttendanceData: (
    data: { studentName: string; studentId: number; eventId: number } | null,
  ) => void;

  // vouchers
  selectedVouchers: Voucher[];
  setSelectedVouchers: (vouchers: Voucher[]) => void;
  toggleVoucher: (voucher: Voucher) => void;

  // login modal
  showLoginModal: boolean;
  setShowLoginModal: (show: boolean) => void;

  // guidance flow for no students
  showGuidanceFlow: boolean;
  guidanceStep:
  | "profile"
  | "add-child"
  | "search-academy"
  | "type-in-course-code"
  | "after-first-payment"
  | null;
  setShowGuidanceFlow: (show: boolean) => void;
  setGuidanceStep: (
    step:
      | "profile"
      | "add-child"
      | "search-academy"
      | "type-in-course-code"
      | "after-first-payment"
      | null,
  ) => void;

  selectedHomeworkStudent: null | Student;
  setSelectedHomeworkStudent: (data: Student | null) => void;
  parentActivateInfo: any;
  childVerify: boolean;
  submitParentActivate: (
    payload: any,
    mode: "activate" | "verify" | "complete",
  ) => Promise<{ success: boolean; data?: any }>;
}

export const useProfileStore = create<ProfileStore>()(
  persist(
    (set, get) => ({
      // util
      loading: false,
      setLoading: (loading) => {
        set({ loading });
      },
      logout: () => {
        // app auth keys
        localStorage.removeItem("parent_token");
        localStorage.removeItem("parent_user");
        localStorage.removeItem("selectedStudent");
        // clear persisted store slice
        localStorage.removeItem("parent-profile-store");
        // reset volatile state
        set(
          {
            loading: false,
            courses: [],
            selectedStudent: null,
            lastVisitedCourseId: null,
            selectedBranchName: null,
            profile: null,
            students: [],
            hasStudentWithSchoolId: false,
            homePageShow: "",
            cartEdit: null,
            attendanceData: null,
            selectedVouchers: [],
            showLoginModal: false,
            showGuidanceFlow: false,
            guidanceStep: null,
            selectedHomeworkStudent: null,
            parentActivateInfo: null,
            childVerify: false,
            mealId: null,
            transportId: null,
          },
          false,
        );
      },

      // course related
      courses: [],
      setCourses: (data) => set({ courses: data }),
      selectedStudent: null,
      setSelectedStudent: (data: Student | null) => {
        set({ selectedStudent: data });
      },
      lastVisitedCourseId: null,
      setLastVisitedCourseId: (path) => set({ lastVisitedCourseId: path }),
      clearLastVisitedCourse: () => set({ lastVisitedCourseId: null }),

      // save meal and transport id
      mealId: null,
      saveMealId: (data) => set({ mealId: data }),
      transportId: null,
      saveTransportId: (data) => set({ transportId: data }),

      // Academy's Branch Course usage
      selectedBranchName: null,
      setSelectedBranchName: (data: string | null) => {
        set({ selectedBranchName: data });
      },

      // user profile related
      profile: null,
      students: [],
      setStudents: (data: Student[]) => set({ students: data }),
      fetchProfile: async () => {
        try {
          const res = await api.get(
            `${process.env.REACT_APP_API_BASE_URL}/user/profile`,
          );
          set({ profile: res.data.data });
        } catch (err) {
          console.error("Failed to fetch profile:", err);
        }
      },
      hasStudentWithSchoolId: false,
      fetchStudents: async () => {
        try {
          const res = await api.get(
            `${process.env.REACT_APP_API_BASE_URL}/student`,
          );
          const students = res.data.result.data;

          let hasStudentWithSchoolId = false;
          let studentWithSchoolId: Student | null = null;

          if (students.length > 0) {
            for (let student of students as Student[]) {
              if ((student as any).school_id) {
                hasStudentWithSchoolId = true;
                studentWithSchoolId = student;
                break;
              }
            }
          } else {
            set({ showGuidanceFlow: true, guidanceStep: "profile" });
          }

          if (hasStudentWithSchoolId) {
            set({
              hasStudentWithSchoolId: true,
              homePageShow: "school",
              selectedStudent: studentWithSchoolId,
            });
          } else {
            api
              .get(
                `${process.env.REACT_APP_API_BASE_URL}/parent/home/search?mode=academy`,
              )
              .then((res2) => {
                if (res2.data.data.items.length == 0) {
                  set({ homePageShow: "explore" });
                } else {
                  set({ homePageShow: "academy" });
                }
              })
              .catch((err2) => {
                console.log("err", err2);
                set({ homePageShow: "explore" });
              })
              .finally(() => {
                set({ loading: false });
              });
          }
          set({ students: students });
          return students;
        } catch (err) {
          console.error("Failed to fetch students:", err);
          return [];
        }
      },
      fetchStudentsRefresh: async () => {
        try {
          const res = await api.get(
            `${process.env.REACT_APP_API_BASE_URL}/student`,
          );
          const students = res.data.result.data;

          set({ showGuidanceFlow: false, guidanceStep: "profile", students });
          return students;
        } catch (err) {
          console.error("Failed to fetch students:", err);
          return [];
        }
      },
      homePageShow: "",
      setHomePageShow: (data: "school" | "academy" | "explore" | "") =>
        set({ homePageShow: data }),
      setProfile: (partial) => {
        set((state) => ({
          profile: state.profile ? { ...state.profile, ...partial } : null,
        }));
      },

      // cart edit
      cartEdit: null,
      setCartEdit: (data) => set({ cartEdit: data }),

      // attendance
      attendanceData: null,
      setAttendanceData: (data) => set({ attendanceData: data }),

      // vouchers
      selectedVouchers: [],
      setSelectedVouchers: (vouchers) => set({ selectedVouchers: vouchers }),
      toggleVoucher: (voucher) => {
        set((state) => ({
          selectedVouchers: state.selectedVouchers.find(
            (v: Voucher) => v.id === voucher.id,
          )
            ? state.selectedVouchers.filter((v: Voucher) => v.id !== voucher.id)
            : [...state.selectedVouchers, voucher],
        }));
      },

      // login modal
      showLoginModal: false,
      setShowLoginModal: (show) => set({ showLoginModal: show }),

      // guidance flow for no students
      showGuidanceFlow: false,
      setShowGuidanceFlow: (show) => set({ showGuidanceFlow: show }),
      guidanceStep: null,
      setGuidanceStep: (step) => set({ guidanceStep: step }),

      selectedHomeworkStudent: null,
      setSelectedHomeworkStudent: (data: Student | null) =>
        set({ selectedHomeworkStudent: data }),

      parentActivateInfo: null,
      childVerify: false,
      submitParentActivate: async (payload: any, mode) => {
        try {
          const res = await api.post(
            `${process.env.REACT_APP_API_BASE_URL}/user/activate`,
            payload,
            {
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              withCredentials: true,
            },
          );
          if (mode === "activate") {
            set({ parentActivateInfo: res.data.data });
            toast.success(
              "Parent account found! Proceed to verification step.",
            );
            return { success: true, data: res.data.data };
          } else if (mode === "verify") {
            if (res.data.data.verified_children) {
              set({ childVerify: true });
              toast.success(
                "Child verified successfully! Proceed with registration.",
              );
              return { success: true, data: res.data.data };
            }
          } else if (mode === "complete") {
            toast.success("Account activation completed! You can now log in.");
            return { success: true, data: res.data.data };
          }
          return { success: false };
        } catch (err) {
          const error = err as AxiosError<{ message: string; data: any }>;
          toast.error(error.response?.data?.message || "An error occurred");
          return { success: false, data: error.response?.data };
        }
      },
    }),
    {
      name: "parent-profile-store",
      storage: createJSONStorage(() => localStorage),
      // only persist what must survive refresh
      partialize: (state) => ({
        profile: state.profile,
        students: state.students,
        selectedStudent: state.selectedStudent,
        lastVisitedCourseId: state.lastVisitedCourseId,
        selectedBranchName: state.selectedBranchName,
        selectedVouchers: state.selectedVouchers,
        homePageShow: state.homePageShow,
        parentActivateInfo: state.parentActivateInfo,
        childVerify: state.childVerify,
        cartEdit: state.cartEdit,
        attendanceData: state.attendanceData,
        showGuidanceFlow: state.showGuidanceFlow,
        guidanceStep: state.guidanceStep,
        // keep loading and modals volatile
      }),
    },
  ),
);
